import { observable, set } from 'mobx';
import { pascalCaseData, toNumber } from '~utils/CaseUtils';
import { formatLoyaltyLevel } from '~utils/FormatterUtils';
import { IRewardsInfo } from '~views/dashboard/loyalty-rewards/ILoyaltyRewards';
import {
    ILoyaltyRewardsResponse,
    IOfferDetailsResponse
} from '~api/interfaces/ILoyaltyRewardsResponse';
import { getSortedOffersInfo, PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';

class Rewards {
    @observable public rewardsDataFormatted: IRewardsInfo[] = [];
    @observable public totalRedeemedPoints: number = 0;

    static fromJSON(rewardsData: ILoyaltyRewardsResponse): Rewards {
        const rewardsOfferInfo = new Rewards();
        const { user: {benefits: offerData}, currentLoyaltyLevel } = rewardsData;

        const rewardsOfferFields = offerData || [];

        const totalRedeemedPoints = rewardsOfferFields?.reduce(
            (redeemedPoints: number, offerDetails) => {
                const inCartStatus =
                    offerDetails?.skuBased ? offerDetails?.sku?.inCart : 0;

                return inCartStatus
                    ? redeemedPoints + toNumber(offerDetails?.points)
                    : redeemedPoints;
            },
            0
        ) as number;

        const filteredOfferFields = rewardsOfferFields?.filter((levelDetails) =>
            !levelDetails?.skuBased
                ? formatLoyaltyLevel(levelDetails?.criteria as string)?.includes(
                      currentLoyaltyLevel
                  )
                : levelDetails
        );

        const rewardsDataFormatted = filteredOfferFields?.map(
            (offerDetails: IOfferDetailsResponse) => ({
                inCart:
                    offerDetails?.skuBased
                        ? offerDetails?.sku?.inCart
                        : offerDetails?.inCart,
                inventoryStatus: offerDetails?.sku?.inventoryStatus?.toLowerCase(),
                meetsCriteria: offerDetails?.meetsCriteria,
                offerButtonLabel: pascalCaseData(offerDetails?.promo?.text?.action as string, true),
                redeemedStatus: offerDetails?.promo?.text?.redeemed,
                offerCode: offerDetails?.offerCode,
                offerDescription: offerDetails?.promo?.text?.description,
                offerImage:
                    offerDetails?.skuBased
                        ? offerDetails?.sku?.largeImage
                        : offerDetails?.image,
                offerLinkAction: offerDetails?.promo?.URL?.action,
                offerPoints: offerDetails?.points,
                offerTitle: offerDetails?.promo?.text?.title,
                offerUsed: offerDetails?.used,
                offerView: offerDetails?.skuBased ? 'ChoicesOfSku' : '',
                productName: offerDetails?.sku?.productName,
                altProductImages: offerDetails?.sku?.altProductImages || [],
                skinType: offerDetails?.sku?.skinType,
                productDescription: offerDetails?.sku?.productDescription,
                shadeName: offerDetails?.sku?.shadeName,
                skuID: offerDetails?.sku?.skuBaseID?.replace('SKU', ''),
                path: offerDetails?.sku?.path,
                pointsNeeded: offerDetails?.pointsNeeded || 0,
                benefits: JSON.parse(offerDetails?.benefits || '[]'),
                highestPriorityClass: null,
                criteriaViewValidations: offerDetails?.criteriaViewValidations,
                criteria: JSON.parse(offerDetails?.criteria || '[]'),
                offerNotificationFields: {
                    criteriaFieldsList: offerDetails?.criteriaFields,
                    criteriaAllowedTierList: offerDetails?.criteriaAllowedTiers,
                    criteriaRequiredSkuList: offerDetails?.criteriaRequiredSkus,
                    inventoryStatus: offerDetails?.sku?.inventoryStatus,
                    pointsNeeded: offerDetails?.pointsNeeded
                }
            })
        );

        const modifiedData = {
            rewardsDataFormatted,
            totalRedeemedPoints: totalRedeemedPoints
        };

        set(rewardsOfferInfo, modifiedData);

        return rewardsOfferInfo;
    }

    public static getSortedRewardsInfo(rewardsDataFormatted: IRewardsInfo[], totalRedeemedPoints: number,
        priorityOrder?: PriorityOrderSort[], categoryOrder?: CategoryOrderSort[]) {
        const rewardsOfferInfo = new Rewards();
        const modifiedData = {
            rewardsDataFormatted: getSortedOffersInfo(rewardsDataFormatted, priorityOrder, categoryOrder),
            totalRedeemedPoints
        };
        set(rewardsOfferInfo, modifiedData);

        return rewardsOfferInfo;
    }
}

export default Rewards;
